import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { GetRequest } from 'src/app/model/maker-checker/get-request';
import { RequestDetail } from 'src/app/model/maker-checker/request-detail';
import { ReviewRequestDetail } from 'src/app/model/maker-checker/review-request-detail';
import { BackofficeService } from 'src/app/services/backoffice.service';
import { LoggingService } from 'src/app/services/logging.service';
import { RequestType } from 'src/app/model/maker-checker/request-type';
import { RequestDetailArrangement } from 'src/app/model/maker-checker/request-detail-arrangement';
import { UpdateRequestDetail } from 'src/app/model/maker-checker/update-request-detail';
import { ColumnMapping } from 'src/app/model/result-grid/column-mapping';
import { Observable, of } from 'rxjs';
import { ResultGridComponent } from '../result-grid/result-grid.component';
import { UpdateRequest } from 'src/app/model/maker-checker/update-request';
import { OperationType } from '../../model/maker-checker/operation-type';
import { AuthService } from 'src/app/services/auth.service';
import { formatDate } from '@angular/common';
import { User } from 'src/app/model/user';
import { ConfirmationModalInputParams } from 'src/app/model/confirmation-modal-input-param';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { messages } from 'src/app/resources/messages';
import { ExportService } from 'src/app/services/export.service';
import { SupportingData } from 'src/app/model/maker-checker/supporting-data';
import { CommonService } from 'src/app/services/common.service';

@Component({
  selector: 'maker-checker-request-details-panel',
  templateUrl: './maker-checker-request-details-panel.component.html',
  styleUrls: ['./maker-checker-request-details-panel.component.scss'],
  providers: [ExportService]
})
export class MakerCheckerRequestDetailsPanelComponent implements OnChanges {

  @ViewChild('requestDetailGrid') requestDetailGrid!: ResultGridComponent;
  @ViewChild('requestDetailsLayoutReferance') requestDetailsLayoutRef: ElementRef<any> | undefined;
  @ViewChild('notesTextAreaReference') notesTextAreaRef!: ElementRef<any>;
  @ViewChild(ConfirmationModalComponent)
  private confirmationModalComponent!: ConfirmationModalComponent;
  @ViewChild('supportingDetailsGrid') supportingDetailsGridReference!: ResultGridComponent;

  @Input() requestDetail!: RequestDetail & RequestType;
  @Output() reloadRequest: EventEmitter<boolean> = new EventEmitter<boolean>();

  public arrangement!: RequestDetailArrangement[];
  public requestGridData!: UpdateRequestDetail[];
  public notes: string = '';
  public serverNotes: string = '';
  public noteMaxLengthValid: string = "valid";
  public loadDetails: boolean = false;
  public isChecker: boolean = false;
  public isMaker: boolean = false;
  public isOpenState: boolean = true;
  public isNoteRequired: boolean = false;
  public msg = messages;
  
  public columnDefsForRequestDetail: ColumnMapping[] = [
    { columnName: "fieldName", columnHeaderName: "Field name", enableTooltip:true },
    { columnName: "existingValue", columnHeaderName: "Existing value", enableTooltip:true},
    { columnName: "requestedValue", columnHeaderName: "Requested value", enableTooltip:true}
  ];
  
  public showSupportingDetails: boolean = false;
  public supportingDetailsTitle: string = '';
  public columnDefsForSupportingDetails!: ColumnMapping[];
  public supportingGridData!: any;

  constructor(private backofficeService: BackofficeService, private loggingService: LoggingService,private authService: AuthService, private commonService: CommonService) { }

  ngOnChanges(changes: SimpleChanges): void {
    for (const property in changes) {
      if (changes.hasOwnProperty(property) && !changes[property].firstChange) {
        if (property === "requestDetail") {
          if (Object.keys(this.requestDetail).length == 0) {
            this.loadDetails = false;
            this.resetControls();
          }
          else {
            this.resetControls();
            this.setrequestdetail(this.requestDetail);
          }
        }
      }
    }
  }

  private resetControls() {
    this.loadDetails = false;
    this.arrangement=[];
    this.requestGridData=[];
    this.notes = '';
    this.serverNotes="";
    this.isNoteRequired = false;
    this.isOpenState = false;
    this.isChecker= false;
    this.isMaker = false;
    this.noteMaxLengthValid = "valid";
    this.showSupportingDetails = false;
    this.supportingDetailsTitle = '';
    this.supportingGridData = [];
    this.columnDefsForSupportingDetails = [];
  }

  private setrequestdetail(requestDetail:RequestDetail & RequestType){

    let request: GetRequest = {
      processId: requestDetail.id,
      requestId: requestDetail.requestId,
      isOpen: requestDetail.status.value == 1
    };
    
    this.backofficeService.getRequestdetail(request).subscribe((returnData: any) => {
      if(returnData.data){
        this.arrangeRequestDetail(returnData.data);
        let timer = setTimeout( () => {
          this.setRequestDetailGrid(returnData.data);
          this.setOperationAccessAndNote(returnData.data);
          this.setSupportingDetails(returnData.data.supportingExtraData);
          clearTimeout(timer);
        }, 100);  
      }
    });
  } 

  arrangeRequestDetail(reviewRequestDetail: ReviewRequestDetail) {
    let sequance = 1;
    this.arrangement.push(
      { fieldName: "Request id", fieldValue: reviewRequestDetail.requestId, column: "column-" + (sequance), sequence: sequance, columnWidth: 3, fieldStyle: "label-value" }
    );
    sequance++

    for (let propertyName in reviewRequestDetail.additionalDetails) {
      this.arrangement.push(
        { fieldName: propertyName, fieldValue: reviewRequestDetail.additionalDetails[propertyName], column: "column-" + (sequance), sequence: sequance, columnWidth: 3, fieldStyle: "label-value" }
      );
      sequance++
    }

    this.arrangement.push(
      { fieldName: "Operation type", fieldValue: reviewRequestDetail.operationType, column: "column-" + (sequance), sequence: sequance, columnWidth: 3, fieldStyle: "label-value" }
    );
    sequance++

    this.arrangement.push(
      { fieldName: "Requested by", fieldValue: reviewRequestDetail.requestedBy?.name, column: "column-" + (sequance), sequence: sequance, columnWidth: 3, fieldStyle: "label-value" }
    );
    sequance++
    
    this.arrangement.push(
      { fieldName: "Requested on (UTC)", fieldValue: formatDate(reviewRequestDetail.requestedOnUtc!, 'dd MMM yyyy h:mm aaa', 'en-US'), column: "column-" + (sequance), sequence: sequance, columnWidth: 3, fieldStyle: "label-value" }
    );
    sequance++

    if (reviewRequestDetail.actionedBy) {
      this.arrangement.push(
        { fieldName: "Recent activity by", fieldValue: reviewRequestDetail.actionedBy?.name, column: "column-" + (sequance), sequence: sequance, columnWidth: 3, fieldStyle: "label-value" }
      );
      sequance++
    }

    if (reviewRequestDetail.actionedOnUtc) {
      this.arrangement.push(
        { fieldName: "Recent activity (UTC)", fieldValue: formatDate(reviewRequestDetail.actionedOnUtc, 'dd MMM yyyy h:mm aaa', 'en-US'), column: "column-" + (sequance), sequence: sequance, columnWidth: 3, fieldStyle: "label-value" }
      );
      sequance++
    }

    let lastcolumnWidth = (3 + (sequance % 4));
    this.arrangement.push(
      { fieldName: "Status", fieldValue: reviewRequestDetail.status.text == "" ? "-" : reviewRequestDetail.status.text, column: "column-" + (sequance), sequence: sequance, columnWidth: lastcolumnWidth, fieldStyle: "custom-label-value" }
    );

    if (this.arrangement.length > 0) {
      this.loadDetails = true;
    }
  }

  setRequestDetailGrid(reviewRequestDetail: ReviewRequestDetail) {
    this.requestGridData = [];
    for (let propertyName in reviewRequestDetail.oldData) {
      this.requestGridData.push(
        { fieldName: propertyName, existingValue: reviewRequestDetail.oldData[propertyName], requestedValue: reviewRequestDetail.newData[propertyName], }
      );
    }

    if (this.requestGridData.length > 0) {
      this.requestDetailGrid.fetch({ pageNumber: 1 });
    }
  }

  confirmAction(operation: number) {

    if (operation == OperationType.Rejected || operation == OperationType.Close) {
      if (!this.notes) {
        this.isNoteRequired = true;
        return;
      }

      if (this.noteMaxLengthValid != 'valid') {
        return;
      }
    }

    const that = this;
    let alertMessage = this.msg.MakerChecker.ConfirmAction.replace("#RequesId", this.requestDetail?.requestId);
    if (operation == OperationType.Approved) {
      alertMessage = alertMessage.replace("#Action", "approve");
    }
    else if (operation == OperationType.Rejected) {
      alertMessage = alertMessage.replace("#Action", "reject");
    }
    else if (operation == OperationType.Close) {
      alertMessage = alertMessage.replace("#Action", "close");
    }

    let param: ConfirmationModalInputParams = {
      msg: alertMessage,
      yesFunc: that.updateRequest.bind(this).bind("operation", operation)
    }
    this.confirmationModalComponent.confirm(param);
  }

  updateRequest(operation: number) {
    let request: UpdateRequest = {
      id: this.requestDetail?.requestId,
      operationalProcessId: this.requestDetail?.id,
      statusId: operation,
      notes: this.notes
    };

    if (operation == OperationType.Approved) {
      this.backofficeService.approveReviewRequest(request).subscribe((returnData: any) => {
        if(returnData.data){
          this.loggingService.logCustomEvent(`Maker Checker - request approved: ${this.requestDetail?.requestId}`);
          this.reloadRequest.emit(true);     
        }
      });
      
    }
    else if (operation == OperationType.Rejected || operation == OperationType.Close) {
      this.backofficeService.updateReviewRequest(request).subscribe((returnData: any) => {
        if (returnData.data) {
          this.loggingService.logCustomEvent(`Maker Checker - request ${operation}: ${this.requestDetail?.requestId}`);
          this.reloadRequest.emit(true);
        }
      });
    }
  }

  setOperationAccessAndNote(reviewRequestDetail: ReviewRequestDetail) {
    this.isOpenState = reviewRequestDetail?.status?.value == OperationType.Open;
    this.serverNotes = reviewRequestDetail?.notes!;
    this.authService.isAuthorized(this.requestDetail.entitlement, false)
      .subscribe(response => {
        if (response) {
          this.isChecker = true;
        }
      });
    
    let xbc_userData: User = this.commonService.getUserData();
    if (xbc_userData?.xbcUserId == reviewRequestDetail.requestedBy?.userId ||  xbc_userData?.emailId == reviewRequestDetail.requestedBy?.email) {
      this.isMaker = true;
    }
  }

  setNotes($event: any) {
    this.notes = $event.detail.value;
    this.isNoteRequired = false;
    if (this.notes?.length > 50) {
      this.noteMaxLengthValid = 'invalid';
    } else {
      this.noteMaxLengthValid = "valid";
    }
  }

  public fetchResult = (): Observable<any> => {
    let response = {
      data: { data: this.requestGridData }
    }

    return of(response);
  }

  public fetchSupportingDetailsResult = (): Observable<any> => {
    let response = {
      data: { data: this.supportingGridData }
    }
    return of(response);
  }

  setSupportingDetails(data: SupportingData) {
    if (data != null && Object.keys(data).length != 0) {
      this.showSupportingDetails = true;
      this.supportingGridData = data.data;
      this.supportingDetailsTitle = data.displayHeader;

      let columns: ColumnMapping[] = [];
      //Get the headers and mapping info
      for (let propertyName in this.supportingGridData[0]) {
        let column: ColumnMapping = { columnHeaderName: propertyName, columnName: propertyName, enableTooltip: true };
        if (propertyName.toLowerCase().includes('date')) {
          column.dateColumn = {
            DateWithTime: true
          };
        }
        if (propertyName.toLowerCase().includes('description')) {
          column.width = 600;
        }
        columns.push(column);
      }
      this.columnDefsForSupportingDetails = columns;
      let timer = setTimeout(() => {
        this.supportingDetailsGridReference.fetch({ pageNumber: 1 });
        clearTimeout(timer);
      }, 100);
    }
  }
}
